/**
 * The app's common control styles — ONE look for buttons, inputs, selects, labels and selection
 * highlights on every page of both dashboards. They are the styles most of the app already
 * used (the Alerts / Search & Track pages), named so a page reaches for a constant instead of
 * re-typing a slightly different class string.
 *
 * Sizes: controls are h-9 (h-8 for the `_SM` variants in dense toolbars), rounded-lg,
 * text-[13px] font-medium. Colours are theme tokens only — never gray-/slate-/blue- literals,
 * which do not follow the dark theme or the brand.
 *
 * Shared building blocks to use instead of hand-rolled markup:
 *   page heading → <PageTitle title subtitle>        tabs → <PageTabs>   view switch → <SegmentedToggle>
 *   pager → <ListPager> / <Pagination>               modal → <Modal>     confirm → <ConfirmDialog>
 *   side filters → <PageWithFilters>+<FilterColumn>  table → <FillHeightCard> + TABLE_HEAD/TABLE_TH/TABLE_ROW
 *   icon-button label → <Tooltip anchored> (or title + aria-label)
 */

/** The box every button shares — for a button with a non-brand fill (e.g. a category colour). */
export const BTN_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors '
  + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-ring disabled:cursor-not-allowed disabled:opacity-50';

/** The page's main action (Add, Save, Apply). */
export const BTN_PRIMARY = `${BTN_BASE} h-9 px-3.5 text-[13px] bg-brand text-white hover:bg-brand-dark`;
/** A secondary action (Cancel, Export, Refresh with a label). */
export const BTN_SECONDARY = `${BTN_BASE} h-9 px-3.5 text-[13px] border border-border bg-card text-foreground hover:bg-muted`;
/** A destructive confirm (Delete). */
export const BTN_DANGER = `${BTN_BASE} h-9 px-3.5 text-[13px] bg-destructive text-white hover:bg-destructive/90`;
/** A quiet text-only action (Clear all, Show more). */
export const BTN_GHOST = `${BTN_BASE} h-9 px-2.5 text-[13px] text-muted-foreground hover:bg-muted hover:text-foreground`;
/** Dense-toolbar variants. */
export const BTN_PRIMARY_SM = `${BTN_BASE} h-8 px-3 text-[12px] bg-brand text-white hover:bg-brand-dark`;
export const BTN_SECONDARY_SM = `${BTN_BASE} h-8 px-3 text-[12px] border border-border bg-card text-foreground hover:bg-muted`;
export const BTN_DANGER_SM = `${BTN_BASE} h-8 px-3 text-[12px] bg-destructive text-white hover:bg-destructive/90`;
/** A square icon-only button (Refresh, Close, More) — give it a Tooltip or title + aria-label. */
export const BTN_ICON =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground '
  + 'transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50';
/** An icon action inside a row or card (edit, delete) — borderless. */
export const BTN_ROW_ICON =
  'inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';

/** Text input, textarea, date input — the filter-column control, full width. */
export const INPUT =
  'h-9 w-full rounded-lg border border-border bg-input px-3 text-[13px] text-foreground placeholder:text-muted-foreground '
  + 'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:cursor-not-allowed disabled:opacity-60';
/** A dense-toolbar input (h-8, not full width) — same box as INPUT otherwise. */
export const INPUT_SM =
  'h-8 rounded-lg border border-border bg-input px-2.5 text-[12px] text-foreground placeholder:text-muted-foreground '
  + 'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:cursor-not-allowed disabled:opacity-60';
/** A multi-line field — INPUT's box, sized by its rows. */
export const TEXTAREA =
  'w-full rounded-lg border border-border bg-input px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground '
  + 'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:cursor-not-allowed disabled:opacity-60';
/** Native <select> — same box as INPUT. */
export const SELECT = `${INPUT} pr-8`;
/** A dense-toolbar <select> (h-8, sized by its content) — same box as INPUT_SM. */
export const SELECT_SM = `${INPUT_SM} pr-7`;
/** A dense search box (h-8) with room for the leading magnifier icon. */
export const SEARCH_INPUT_SM = `${INPUT_SM} w-full pl-8`;
/** A search box: INPUT with room for the leading magnifier icon (absolute left-2.5). */
export const SEARCH_INPUT = `${INPUT} pl-8`;
/** A form field's label. */
export const LABEL = 'mb-1 block text-[12px] font-medium text-foreground';
/** Helper / hint text under a field. */
export const HINT = 'mt-1 text-[11px] text-muted-foreground';

/** A section heading inside a page or card. */
export const SECTION_TITLE = 'text-[13px] font-semibold text-foreground';
/** A small uppercase eyebrow (filter sections, table heads). */
export const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';

/** A white content card on the page background. */
export const CARD = 'rounded-xl border border-border bg-card shadow-card';

/** Hover for a clickable row / list item. */
export const HOVER_ROW = 'transition-colors hover:bg-muted';
/** The selected row / list item / option (with HOVER_ROW for the rest). */
export const SELECTED = 'bg-brand-light text-foreground';
/** A selected card or tile: brand border + light tint. */
export const SELECTED_CARD = 'border-brand bg-brand-lighter ring-1 ring-brand';
